import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  QrCode2 as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import { formatInBusinessTimezone } from '../utils/dateTimeHelper';

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const OnlinePaymentScreen = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totalPayable, setTotalPayable] = useState(0);
  const [paymentProviders, setPaymentProviders] = useState(undefined);
  const [studentInfo, setStudentInfo] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [qrInstructions, setQrInstructions] = useState('');
  const [qrImageError, setQrImageError] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await Promise.all([loadData(), loadPaymentProviders(), loadStudentProfile()]);
  };

  const loadStudentProfile = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/master/student/get-student-profile', {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setStudentInfo(response.data);
    } catch (error) {
      console.error('Error loading student profile:', error);
      handleCatch(error);
    }
  };

  const loadPaymentProviders = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/school-common/payment-provider', {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setPaymentProviders(response.data);
    } catch (error) {
      console.error('Error loading payment providers:', error);
      handleCatch(error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/school/school-transaction/get-pending-bills-student', {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const datas = response.data;
      let tempItems = [];
      let total = 0;
      datas.forEach((data) => {
        tempItems.push({
          isChecked: true,
          monthName: data.monthName,
          duesAmt: data.duesAmt,
          billId: data.id,
          dueDate: data.dueDate,
        });
        total += data.duesAmt;
      });
      setTotalPayable(total);
      setItems(tempItems);
    } catch (error) {
      console.error('Error loading pending bills:', error);
      handleCatch(error);
      setNotification({ open: true, message: 'Failed to load pending bills', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const setChecked = (index, value) => {
    const tempItems = [...items];
    tempItems[index].isChecked = value;
    let total = 0;
    tempItems.forEach((item) => { if (item.isChecked) total += item.duesAmt; });
    setTotalPayable(total);
    setItems(tempItems);
  };

  const submitPayment = async (paymentID) => {
    try {
      setLoading(true);
      const billIds = items.filter((i) => i.isChecked).map((i) => ({ billId: i.billId }));
      const data = { referenceNo: 'Online', remarks: paymentID, billids: billIds };
      const token = localStorage.getItem('userToken');
      await api.post('api/school/school-transaction/save-fee-receipt-online-payment', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotification({ open: true, message: 'Payment successful! Receipt generated.', severity: 'success' });
      await loadData();
    } catch (error) {
      console.error('Error submitting payment:', error);
      handleCatch(error);
      setNotification({ open: true, message: 'Failed to process payment', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRazorpayPayment = async (provider) => {
    try {
      let settings = {};
      const raw = provider.additionalSettings;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') settings = parsed;
        else if (typeof parsed === 'string') settings = { key_id: parsed };
      } catch (e) {
        // additionalSettings is a plain key string, not JSON
        if (raw && typeof raw === 'string') settings = { key_id: raw.trim() };
      }

      const amountPaise = totalPayable * 100;

      // ── Native app: use the RN bridge → react-native-razorpay ───────────
      if (window.NativeBridge?.isNative) {
        if (!settings.key_id) {
          setNotification({ open: true, message: 'Payment gateway not configured (missing key)', severity: 'error' });
          return;
        }
        const result = await window.NativeBridge.openPayment({
          key:          settings.key_id   || '',
          amount:       amountPaise,
          currency:     settings.currency || 'INR',
          name:         'Edutron School App',
          description:  settings.description || 'School Fee Payment',
          image:        '',
          order_id:     '',
          email:        studentInfo?.email       || '',
          contact:      studentInfo?.mobile      || '',
          prefill_name: studentInfo?.studentName || '',
          theme_color:  '#003D82',
        });
        if (result?.success) {
          await submitPayment(result.razorpay_payment_id);
        } else if (result?.code === 0) {
          setNotification({ open: true, message: 'Payment cancelled', severity: 'warning' });
        } else {
          setNotification({ open: true, message: result?.description || 'Payment failed', severity: 'error' });
        }
        return;
      }

      // ── Web browser: load Razorpay checkout.js ───────────────────────────
      const res = await loadRazorpayScript();
      if (!res) {
        setNotification({ open: true, message: 'Failed to load payment gateway.', severity: 'error' });
        return;
      }
      const options = {
        description: settings.description || 'School Fee Payment',
        currency: settings.currency || 'INR',
        key: settings.key_id,
        amount: amountPaise,
        name: 'Edutron School App',
        prefill: {
          email: studentInfo?.email || '',
          contact: studentInfo?.mobile || '',
          name: studentInfo?.studentName || '',
        },
        theme: { color: '#003D82' },
        handler: function (response) { submitPayment(response.razorpay_payment_id); },
        modal: {
          ondismiss: function () {
            setNotification({ open: true, message: 'Payment cancelled', severity: 'warning' });
          },
        },
      };
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error('Razorpay payment error:', error);
      setNotification({ open: true, message: error?.message || 'Payment initialization failed.', severity: 'error' });
    }
  };

  const handleQRPayment = (provider) => {
    let settings = {};
    try { settings = JSON.parse(provider.additionalSettings); } catch (e) { console.error(e); }
    const imageUrl = settings.imageUrl;
    if (!imageUrl || imageUrl.trim() === '') {
      setNotification({ open: true, message: 'QR code image URL is not configured', severity: 'error' });
      return;
    }
    setQrImageError(false);
    setQrModalVisible(true);
    setQrImageUrl(imageUrl);
    setQrInstructions(settings.instructions || 'Scan this QR code to make payment');
  };

  const onRefresh = async () => { await loadInitialData(); };

  const selectedCount = items.filter((i) => i.isChecked).length;

  return (
    <Box sx={{ bgcolor: '#F5F7FA', minHeight: '100vh' }}>
      <MobileHeader
        title="Pay Fees"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      {loading ? (
        <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>Loading payment info...</Typography>
        </Box>
      ) : paymentProviders === undefined || paymentProviders?.length === 0 ? (
        /* No gateway configured */
        <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3 }}>
          <Typography sx={{ fontSize: 64, mb: 1.5 }}></Typography>
          <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
            No Payment Gateway
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#666', textAlign: 'center', mb: 3 }}>
            Online payment is currently unavailable. Please contact the school office.
          </Typography>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => loadPaymentProviders()} sx={{ borderRadius: 20 }}>
            Check Again
          </Button>
        </Box>
      ) : items.length === 0 ? (
        /* No pending bills */
        <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#4CAF50', mb: 2 }} />
          <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
            No Pending Bills
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#666', textAlign: 'center' }}>
            You don't have any pending bills at the moment.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Summary bar */}
          <Box sx={{ bgcolor: colors.BLUE, px: 2, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: '#fff', fontSize: 13 }}>
              {selectedCount} of {items.length} bills selected
            </Typography>
            <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>
              ₹{totalPayable}
            </Typography>
          </Box>

          {/* Scrollable bill list */}
          <Box sx={{ px: 1, pt: 1, pb: 4 }}>
            {items.map((item, index) => (
              <Box
                key={index}
                onClick={() => setChecked(index, !item.isChecked)}
                sx={{
                  bgcolor: item.isChecked ? '#EEF5FF' : '#fff',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  borderLeft: `4px solid ${item.isChecked ? colors.BLUE : '#e0e0e0'}`,
                  mx: 0.5,
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  px: 1.5,
                  py: 1.2,
                }}
              >
                <Checkbox
                  checked={item.isChecked}
                  onChange={(e) => { e.stopPropagation(); setChecked(index, e.target.checked); }}
                  sx={{ color: colors.BLUE, p: 0.5, mr: 1 }}
                  size="small"
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: '#222' }}>
                    {item.monthName == null ? 'Admission Fee' : item.monthName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#888', mt: 0.2 }}>
                    Due: {formatInBusinessTimezone(item.dueDate, 'dd MMM yyyy')}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 'bold', fontSize: 16, color: item.isChecked ? colors.BLUE : '#ccc' }}>
                  ₹{item.duesAmt}
                </Typography>
              </Box>
            ))}

            {/* Payment options */}
            {selectedCount > 0 && (
              <Box sx={{ mx: 0.5, mt: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.8 }}>
                  Payment Options
                </Typography>
                {paymentProviders.map((provider) => {
                  if (provider.providerName === 'StaticImage') {
                    return (
                      <Button
                        key={provider.id}
                        variant="contained"
                        fullWidth
                        startIcon={<QrCodeIcon />}
                        onClick={() => handleQRPayment(provider)}
                        sx={{ bgcolor: '#000', borderRadius: 22, mb: 1.5, height: 48, textTransform: 'none', fontWeight: 'bold', '&:hover': { bgcolor: '#333' } }}
                      >
                        Pay via QR Code
                      </Button>
                    );
                  } else if (provider.providerName === 'RazorPay') {
                    return (
                      <Button
                        key={provider.id}
                        variant="contained"
                        fullWidth
                        startIcon={<CreditCardIcon />}
                        onClick={() => handleRazorpayPayment(provider)}
                        disabled={totalPayable === 0}
                        sx={{ bgcolor: colors.BLUE, borderRadius: 22, mb: 1.5, height: 48, textTransform: 'none', fontWeight: 'bold', '&:hover': { bgcolor: '#0052A3' } }}
                      >
                        Pay ₹{totalPayable} via Razorpay
                      </Button>
                    );
                  } else {
                    return (
                      <Button key={provider.id} variant="outlined" fullWidth disabled startIcon={<PaymentIcon />}
                        sx={{ borderRadius: 22, mb: 1.5, height: 48, textTransform: 'none' }}>
                        {provider.providerName} (Not Supported)
                      </Button>
                    );
                  }
                })}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* QR Code Modal */}
      <Dialog open={qrModalVisible} onClose={() => setQrModalVisible(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, m: 1 } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 16 }}>
          Payment QR Code
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#666', mb: 2, textAlign: 'center' }}>
            {qrInstructions}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
            {qrImageError ? (
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: '#d32f2f', mb: 2 }}>Failed to load QR code</Typography>
                <Button variant="contained" size="small" onClick={() => setQrImageError(false)}>Retry</Button>
              </Box>
            ) : (
              <img
                src={qrImageUrl}
                alt="Payment QR Code"
                style={{ maxWidth: '100%', maxHeight: 350, objectFit: 'contain', borderRadius: 8 }}
                onError={() => setQrImageError(true)}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button fullWidth variant="contained" onClick={() => setQrModalVisible(false)}
            sx={{ borderRadius: 22, height: 44, textTransform: 'none', fontWeight: 'bold' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnlinePaymentScreen;
